import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Star, Plus, Trash, PencilSimple, LinkSimple, Chat, ArrowRight,
  FilePdf, UserPlus, Paperclip, ClipboardText,
} from "@phosphor-icons/react";
import {
  DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable,
  DragOverlay,
} from "@dnd-kit/core";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import InterviewDialog from "@/components/hr/InterviewDialog";

const STAGES = [
  { k: "cv_received", l: "CV recibido", color: "bg-slate-100 text-slate-700 border-slate-200" },
  { k: "screening", l: "Filtro CV", color: "bg-blue-100 text-blue-800 border-blue-200" },
  { k: "interview", l: "Entrevista", color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  { k: "references", l: "Referencias", color: "bg-purple-100 text-purple-800 border-purple-200" },
  { k: "offer", l: "Oferta", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { k: "hired", l: "Contratado", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { k: "rejected", l: "Rechazado", color: "bg-red-100 text-red-700 border-red-200" },
];

const CREWS = ["A", "B", "C", "N/A"];
const REGIMES = ["14x7", "10x4", "20x10", "5x2"];
const ROLES = [
  { v: "operativo", l: "Operativo" },
  { v: "mantenimiento", l: "Mantenimiento" },
  { v: "almacen", l: "Almacén" },
  { v: "campamento", l: "Campamento" },
  { v: "residente_mina", l: "Residente de mina" },
  { v: "gerente_proyecto", l: "Gerente de proyecto" },
  { v: "administrativo", l: "Administrativo" },
  { v: "jefatura", l: "Jefatura" },
];

const emptyCand = () => ({
  name: "", email: "", phone: "", stage: "cv_received", source: "",
  resume_url: "", expected_salary: 0, currency: "MXN", location: "",
  experience_years: 0, notes: "", rating: 0,
});

const emptyHire = () => ({
  dni: "", position: "", area: "", role: "operativo", crew: "A",
  regime: "14x7", shift_type: "day", shift_start_date: "",
  employer: "propia", contractor_name: "", hire_date: "",
});

function CandidateCard({ cand, canManage, onEdit, onDelete, onNotes, onHire, onInterview }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: cand.id });
  return (
    <li
      ref={setNodeRef}
      data-testid={`cand-${cand.id}`}
      className={"bg-white border border-slate-200 rounded-md p-2 shadow-sm " + (isDragging ? "opacity-40" : "")}
    >
      <div {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing" data-testid={`cand-drag-${cand.id}`}>
        <p className="text-sm font-semibold text-slate-900 truncate">{cand.name}</p>
        <p className="text-[10px] text-slate-500 mt-0.5">{cand.source || "-"} · {cand.experience_years}a exp.</p>
        {cand.rating > 0 && (
          <div className="flex items-center gap-0.5 mt-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} size={9} weight={n <= cand.rating ? "fill" : "regular"} className={n <= cand.rating ? "text-amber-500" : "text-slate-300"} />
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 mt-2 flex-wrap">
        {cand.resume_url && (
          <a href={cand.resume_url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-700 hover:underline flex items-center gap-0.5">
            <LinkSimple size={10} /> CV
          </a>
        )}
        {cand.resume_file && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/hr/candidates/${cand.id}/resume`}
             target="_blank" rel="noreferrer"
             data-testid={`cand-resume-${cand.id}`}
             className="text-[10px] text-emerald-700 hover:underline flex items-center gap-0.5 font-semibold"
             title={cand.resume_filename || "Ver adjunto"}>
            <Paperclip size={10} weight="bold" /> Adjunto
          </a>
        )}
        <button data-testid={`cand-notes-${cand.id}`} onClick={() => onNotes(cand)} className="text-[10px] text-slate-500 hover:text-blue-900 flex items-center gap-0.5">
          <Chat size={10} /> {(cand.interview_notes || []).length}
        </button>
        {canManage && (
          <button data-testid={`cand-interview-${cand.id}`} onClick={() => onInterview(cand)}
                   className="text-[10px] text-indigo-700 hover:text-indigo-900 flex items-center gap-0.5 font-semibold"
                   title="Realizar entrevista técnica">
            <ClipboardText size={10} weight="bold" /> Entrevista
          </button>
        )}
        {canManage && cand.stage === "hired" && !cand.hired_employee_id && (
          <button data-testid={`cand-hire-${cand.id}`} onClick={() => onHire(cand)} title="Convertir en empleado" className="text-emerald-600 hover:text-emerald-800 ml-auto flex items-center gap-0.5 text-[10px] font-semibold">
            <UserPlus size={10} weight="bold" /> Contratar
          </button>
        )}
        {canManage && cand.hired_employee_id && (
          <span className="ml-auto text-[9px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded font-semibold" data-testid={`cand-hired-${cand.id}`}>✓ Empleado</span>
        )}
        {canManage && !(cand.stage === "hired" && !cand.hired_employee_id) && !cand.hired_employee_id && (
          <div className="ml-auto flex gap-1">
            <button data-testid={`cand-edit-${cand.id}`} onClick={() => onEdit(cand)} className="text-slate-400 hover:text-blue-900"><PencilSimple size={10} /></button>
            <button data-testid={`cand-delete-${cand.id}`} onClick={() => onDelete(cand.id)} className="text-slate-400 hover:text-red-500"><Trash size={10} /></button>
          </div>
        )}
      </div>
    </li>
  );
}

function StageColumn({ stage, items, canManage, onEdit, onDelete, onNotes, onHire, onInterview }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.k });
  const borderClass = stage.color.split(" ").find((c) => c.startsWith("border-")) || "border-slate-200";
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-col-${stage.k}`}
      className={"rounded-lg border p-2 min-h-32 transition-colors " + borderClass + (isOver ? " bg-blue-50/60 ring-2 ring-blue-300" : "")}
    >
      <div className={"text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded " + stage.color}>
        {stage.l} <span className="opacity-60">({items.length})</span>
      </div>
      <ul className="mt-2 space-y-2">
        {items.map((c) => (
          <CandidateCard key={c.id} cand={c} canManage={canManage} onEdit={onEdit} onDelete={onDelete} onNotes={onNotes} onHire={onHire} onInterview={onInterview} />
        ))}
        {items.length === 0 && <p className="text-[10px] text-slate-400 italic text-center py-4">Vacío</p>}
      </ul>
    </div>
  );
}

/**
 * Mini-ATS: Kanban view of candidates per vacancy with drag-and-drop between
 * stages, PDF pipeline export, and one-click "hire → create employee" flow.
 */
export default function CandidatesModal({ vacancyId, vacancyTitle, canManage, open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState(emptyCand());
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [notesFor, setNotesFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [hireFor, setHireFor] = useState(null);
  const [hireForm, setHireForm] = useState(emptyHire());
  const [interviewFor, setInterviewFor] = useState(null);   // Iter135

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = useCallback(() => {
    if (!vacancyId) return;
    api.get(`/hr/vacancies/${vacancyId}/candidates`).then((r) => setItems(r.data)).catch(() => {});
    api.get(`/hr/vacancies/${vacancyId}/candidates/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, [vacancyId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    try {
      const payload = {
        ...form,
        expected_salary: Number(form.expected_salary) || 0,
        experience_years: Number(form.experience_years) || 0,
        rating: Number(form.rating) || 0,
      };
      if (editing) await api.put(`/hr/candidates/${editing}`, payload);
      else await api.post(`/hr/vacancies/${vacancyId}/candidates`, payload);
      toast.success("Candidato guardado");
      setFormOpen(false); setEditing(null); setForm(emptyCand()); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar candidato?")) return;
    try { await api.delete(`/hr/candidates/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const moveStage = async (candId, newStage) => {
    const cand = items.find((c) => c.id === candId);
    if (!cand || cand.stage === newStage) return;
    const optimistic = items.map((c) => (c.id === candId ? { ...c, stage: newStage } : c));
    setItems(optimistic);
    try {
      const { id: _id, vacancy_id: _v, applied_at: _a, stage_history: _h, interview_notes: _n, hired_employee_id: _he, created_by: _c, ...rest } = cand;
      await api.put(`/hr/candidates/${candId}`, { ...rest, stage: newStage });
      load();
    } catch (e) {
      toast.error(apiError(e));
      load();
    }
  };

  const addNote = async () => {
    if (!noteText.trim() || !notesFor) return;
    try {
      await api.post(`/hr/candidates/${notesFor.id}/notes`, { note: noteText });
      toast.success("Nota agregada");
      setNoteText("");
      const r = await api.get(`/hr/vacancies/${vacancyId}/candidates`);
      setItems(r.data);
      setNotesFor(r.data.find((c) => c.id === notesFor.id));
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadPipeline = async () => {
    try {
      const res = await api.get(`/hr/vacancies/${vacancyId}/candidates/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pipeline-${(vacancyTitle || vacancyId).replace(/\s+/g, "-").toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openHire = (cand) => {
    setHireFor(cand);
    setHireForm({ ...emptyHire(), hire_date: new Date().toISOString().slice(0, 10) });
  };

  const submitHire = async () => {
    if (!hireFor) return;
    try {
      await api.post(`/hr/candidates/${hireFor.id}/hire`, hireForm);
      toast.success("Candidato convertido a empleado");
      setHireFor(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const editOpenCand = (c) => {
    setEditing(c.id);
    setForm({ ...emptyCand(), ...c });
    setFormOpen(true);
  };

  const byStage = (k) => items.filter((c) => c.stage === k);
  const activeCand = activeId ? items.find((c) => c.id === activeId) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="candidates-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Candidatos — {vacancyTitle}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          {stats && (
            <div className="flex items-center gap-4 text-xs">
              <span className="text-slate-500">Total: <b className="text-slate-900">{stats.total}</b></span>
              {STAGES.map((s) => (
                <span key={s.k} className="text-slate-500">{s.l}: <b className="text-slate-900">{stats.by_stage[s.k] || 0}</b></span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <Button data-testid="cand-pdf" size="sm" variant="outline" onClick={downloadPipeline} className="gap-1">
              <FilePdf size={14} /> PDF pipeline
            </Button>
            {canManage && (
              <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) { setEditing(null); setForm(emptyCand()); } }}>
                <DialogTrigger asChild>
                  <Button data-testid="cand-new" size="sm" className="bg-blue-900 hover:bg-blue-800 gap-1">
                    <Plus size={14} weight="bold" /> Nuevo candidato
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} candidato</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label className="overline-label">Nombre *</Label><Input data-testid="cand-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                    <div><Label className="overline-label">Correo</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                    <div><Label className="overline-label">Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                    <div><Label className="overline-label">Etapa</Label>
                      <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                        <SelectTrigger data-testid="cand-stage"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">{STAGES.map((s) => <SelectItem key={s.k} value={s.k}>{s.l}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div><Label className="overline-label">Origen</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="LinkedIn, referido, portal…" /></div>
                    <div><Label className="overline-label">Ubicación</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
                    <div><Label className="overline-label">Años exp.</Label><Input type="number" min={0} value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} /></div>
                    <div><Label className="overline-label">Sueldo pretendido</Label><Input type="number" min={0} value={form.expected_salary} onChange={(e) => setForm({ ...form, expected_salary: e.target.value })} /></div>
                    <div><Label className="overline-label">Rating (0-5)</Label><Input type="number" min={0} max={5} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></div>
                    <div className="col-span-2"><Label className="overline-label">Link a CV</Label><Input value={form.resume_url} onChange={(e) => setForm({ ...form, resume_url: e.target.value })} placeholder="https://drive.google.com/..." /></div>
                    <div className="col-span-2"><Label className="overline-label">Notas</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                  </div>
                  <Button data-testid="cand-save" onClick={save} disabled={!form.name} className="bg-blue-900 hover:bg-blue-800 w-full mt-2">
                    {editing ? "Actualizar" : "Agregar candidato"}
                  </Button>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {canManage && (
          <p className="text-[10px] text-slate-400 italic mb-1">Tip: arrastra las tarjetas entre columnas para cambiar la etapa.</p>
        )}

        {/* Kanban board with drag-and-drop */}
        <DndContext
          sensors={sensors}
          onDragStart={(e) => setActiveId(e.active.id)}
          onDragEnd={(e) => {
            setActiveId(null);
            if (canManage && e.over && e.active) moveStage(e.active.id, e.over.id);
          }}
          onDragCancel={() => setActiveId(null)}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mt-3" data-testid="candidates-kanban">
            {STAGES.map((s) => (
              <StageColumn
                key={s.k}
                stage={s}
                items={byStage(s.k)}
                canManage={canManage}
                onEdit={editOpenCand}
                onDelete={remove}
                onNotes={setNotesFor}
                onHire={openHire}
                onInterview={setInterviewFor}
              />
            ))}
          </div>
          <DragOverlay>
            {activeCand && (
              <div className="bg-white border border-blue-400 rounded-md p-2 shadow-lg text-sm font-semibold">
                {activeCand.name}
              </div>
            )}
          </DragOverlay>
        </DndContext>

        {/* Interview notes drawer */}
        {interviewFor && (
          <InterviewDialog
            candidate={interviewFor}
            open={Boolean(interviewFor)}
            onClose={() => setInterviewFor(null)}
            onSaved={load}
          />
        )}

        {/* Interview notes drawer */}
        {notesFor && (
          <Dialog open={Boolean(notesFor)} onOpenChange={(o) => { if (!o) { setNotesFor(null); setNoteText(""); } }}>
            <DialogContent className="bg-white max-w-lg">
              <DialogHeader><DialogTitle>Notas de entrevista — {notesFor.name}</DialogTitle></DialogHeader>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(notesFor.interview_notes || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Sin notas todavía.</p>
                ) : (notesFor.interview_notes || []).map((n, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded p-2">
                    <p className="text-xs font-semibold text-slate-700">{n.interviewer} <span className="text-slate-400 font-normal">· {(n.at || "").slice(0, 10)}</span></p>
                    <p className="text-sm text-slate-900 mt-1">{n.note}</p>
                  </div>
                ))}
              </div>
              {canManage && (
                <div className="flex items-end gap-2 mt-2">
                  <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} placeholder="Nueva nota de entrevista…" data-testid="cand-note-text" />
                  <Button size="sm" onClick={addNote} disabled={!noteText.trim()} data-testid="cand-note-save" className="bg-blue-900 hover:bg-blue-800">
                    <ArrowRight size={12} />
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* Hire → create employee modal */}
        {hireFor && (
          <Dialog open={Boolean(hireFor)} onOpenChange={(o) => { if (!o) setHireFor(null); }}>
            <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="hire-modal">
              <DialogHeader>
                <DialogTitle>Contratar — {hireFor.name}</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-slate-500 -mt-2">Se creará un empleado activo en el módulo de HR con los datos del candidato.</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="overline-label">DNI *</Label><Input data-testid="hire-dni" value={hireForm.dni} onChange={(e) => setHireForm({ ...hireForm, dni: e.target.value })} /></div>
                <div><Label className="overline-label">Puesto *</Label><Input data-testid="hire-position" value={hireForm.position} onChange={(e) => setHireForm({ ...hireForm, position: e.target.value })} placeholder="Perforista, Mecánico A…" /></div>
                <div><Label className="overline-label">Área</Label><Input value={hireForm.area} onChange={(e) => setHireForm({ ...hireForm, area: e.target.value })} /></div>
                <div><Label className="overline-label">Rol</Label>
                  <Select value={hireForm.role} onValueChange={(v) => setHireForm({ ...hireForm, role: v })}>
                    <SelectTrigger data-testid="hire-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="overline-label">Cuadrilla</Label>
                  <Select value={hireForm.crew} onValueChange={(v) => setHireForm({ ...hireForm, crew: v })}>
                    <SelectTrigger data-testid="hire-crew"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{CREWS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="overline-label">Régimen</Label>
                  <Select value={hireForm.regime} onValueChange={(v) => setHireForm({ ...hireForm, regime: v })}>
                    <SelectTrigger data-testid="hire-regime"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{REGIMES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="overline-label">Turno</Label>
                  <Select value={hireForm.shift_type} onValueChange={(v) => setHireForm({ ...hireForm, shift_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="day">Diurno</SelectItem>
                      <SelectItem value="night">Nocturno</SelectItem>
                      <SelectItem value="rotating">Rotativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="overline-label">Inicio de ciclo</Label><Input type="date" value={hireForm.shift_start_date} onChange={(e) => setHireForm({ ...hireForm, shift_start_date: e.target.value })} /></div>
                <div><Label className="overline-label">Empleador</Label>
                  <Select value={hireForm.employer} onValueChange={(v) => setHireForm({ ...hireForm, employer: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="propia">Propia</SelectItem>
                      <SelectItem value="contratista">Contratista</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="overline-label">Contratista</Label><Input value={hireForm.contractor_name} onChange={(e) => setHireForm({ ...hireForm, contractor_name: e.target.value })} disabled={hireForm.employer !== "contratista"} /></div>
                <div><Label className="overline-label">Fecha de ingreso</Label><Input type="date" value={hireForm.hire_date} onChange={(e) => setHireForm({ ...hireForm, hire_date: e.target.value })} /></div>
              </div>
              <Button
                data-testid="hire-submit"
                onClick={submitHire}
                disabled={!hireForm.dni || !hireForm.position}
                className="bg-emerald-700 hover:bg-emerald-800 w-full mt-2"
              >
                Crear empleado
              </Button>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
